import { useEffect, useMemo, useState } from 'react'
import { StudentLanguageMaterial } from '@interfaces/StudentLanguageMaterial'

interface UseSkillsTutorSelectionParams {
  activeStudentLanguageProfile?: {
    EligibleForNewSkillsTutorAssignment?: boolean
    SkillsAttemptsRemaining?: number
    RecordId?: string
  } | null
  skillsTutorMaterials?: StudentLanguageMaterial[]
  skillsTutorMaterialsLoaded?: boolean
}

export interface SkillsTutorSelectionState {
  isModalOpen: boolean
  openModal: () => void
  closeModal: () => void
  assignedSkillsTutorMaterials: StudentLanguageMaterial[]
  completedSkillsTutorMaterials: StudentLanguageMaterial[]
  remainingSwitches: number
  studentLanguageProfileId: string
}

/**
 * Encapsulates SkillsTutor selection state: which materials are assigned/completed
 * and whether the selection modal should be open.
 */
export const useSkillsTutorSelection = ({
  activeStudentLanguageProfile,
  skillsTutorMaterials,
  skillsTutorMaterialsLoaded,
}: UseSkillsTutorSelectionParams): SkillsTutorSelectionState => {
  const assignedSkillsTutorMaterials = useMemo(
    () =>
      (skillsTutorMaterials || []).filter(
        (material) =>
          material?.IsSkillsTutorMaterial === true &&
          material?.SkillsTutorCompleted === false &&
          material?.Status === 'Assigned'
      ),
    [skillsTutorMaterials]
  )

  const completedSkillsTutorMaterials = useMemo(
    () =>
      (skillsTutorMaterials || []).filter(
        (material) => material?.IsSkillsTutorMaterial === true && material?.SkillsTutorCompleted === true
      ),
    [skillsTutorMaterials]
  )

  // Start with the modal closed; decide whether to auto-open once data is loaded and conditions are met.
  const [isModalOpen, setIsModalOpen] = useState(false)

  const openModal = () => setIsModalOpen(true)
  const closeModal = () => setIsModalOpen(false)

  const remainingSwitches = activeStudentLanguageProfile?.SkillsAttemptsRemaining || 0
  const studentLanguageProfileId = activeStudentLanguageProfile?.RecordId || ''

  // Auto-open the modal only when:
  // - materials have been loaded
  // - the student is eligible for a new Skills Tutor assignment
  // - there are no currently-assigned (in-progress) Skills Tutors
  // - there is at least one remaining switch attempt
  useEffect(() => {
    const materialsLoaded = Boolean(skillsTutorMaterialsLoaded)
    const eligible = Boolean(activeStudentLanguageProfile?.EligibleForNewSkillsTutorAssignment)
    const noAssigned = assignedSkillsTutorMaterials.length === 0
    const hasRemainingSwitches = remainingSwitches > 0

    if (materialsLoaded && eligible && noAssigned && hasRemainingSwitches) {
      setIsModalOpen(true)
    }
  }, [
    skillsTutorMaterialsLoaded,
    assignedSkillsTutorMaterials.length,
    activeStudentLanguageProfile?.EligibleForNewSkillsTutorAssignment,
    remainingSwitches,
  ])

  return {
    isModalOpen,
    openModal,
    closeModal,
    assignedSkillsTutorMaterials,
    completedSkillsTutorMaterials,
    remainingSwitches,
    studentLanguageProfileId,
  }
}

